// components/LaundryDetails/LaundryDetailsTable.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { dubaiDate } from '../../../../utils/dubaiTime';

const itemPriority = [
    'Bedsheet', 'Small Bedsheet', 'Super King Bedsheet', 'Duvet Cover', 'Small Duvet Cover',
    'Super King Duvet Cover', 'Pillowcase', 'Bath Towel', 'Hand Towel', 'Face Towel', 'Bathmat', 'Beach Towel'
];

const LaundryDetailsTable = ({ bookingId }) => {
    const [currentLaundry, setCurrentLaundry] = useState(null);
    const [latestLaundry, setLatestLaundry] = useState(null);
    const [nextLaundry, setNextLaundry] = useState(null);
    const [standardQuantities, setStandardQuantities] = useState({});
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    const sortItemsByPriority = (items) => {
        return items.sort((a, b) => {
            const aPriority = itemPriority.indexOf(a.name);
            const bPriority = itemPriority.indexOf(b.name);
            if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
            if (aPriority !== -1) return -1;
            if (bPriority !== -1) return 1;
            return a.name.localeCompare(b.name);
        });
    };

    const calculateStandardQuantity = (bedrooms, bathrooms) => {
        const adjustedBedrooms = bedrooms === 0 ? 1 : bedrooms;
        return {
            'Bedsheet': adjustedBedrooms,
            'Duvet Cover': adjustedBedrooms,
            'Pillowcase': adjustedBedrooms * 4,
            'Bath Towel': adjustedBedrooms * 2,
            'Hand Towel': adjustedBedrooms * 2,
            'Face Towel': adjustedBedrooms * 2,
            'Bathmat': Math.ceil(bathrooms * 1),
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const currentRes = await axios.get(`/api/laundry/${bookingId}/laundry-count-items`);
                setCurrentLaundry(currentRes.data);

                // PREVIOUS (last before this booking)
                try {
                    const latestRes = await axios.get(`/api/laundry/last-count-before/${bookingId}`);
                    setLatestLaundry(latestRes.data || null);
                } catch (err) {
                    console.warn("No previous laundry count found. Proceeding without latest comparison.");
                    setLatestLaundry(null);
                }

                // NEXT (first after this booking)
                try {
                    const nextRes = await axios.get(`/api/laundry/next-count-after/${bookingId}`);
                    setNextLaundry(nextRes.data || null);
                } catch (err) {
                    console.warn("No next laundry count found. Proceeding without forward navigation.");
                    setNextLaundry(null);
                }

                if (currentRes.data.is_custom_sq && currentRes.data.property_id) {
                    const sqRes = await axios.get(`/api/properties/property-sq/${currentRes.data.property_id}`);
                    const customSQ = sqRes.data.reduce((acc, item) => {
                        acc[item.laundry_item_name] = item.count;
                        return acc;
                    }, {});
                    setStandardQuantities(customSQ);
                } else {
                    setStandardQuantities(
                        calculateStandardQuantity(currentRes.data.bedrooms, currentRes.data.bathrooms)
                    );
                }
            } catch (err) {
                console.error("Error loading laundry data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [bookingId]);

    if (loading || !currentLaundry) return (
        <div className="app-loading">
            <div className="app-spinner" />
            <div className="app-spinner-text">Loading Laundry...</div>
        </div>
    );

    const latestItems = latestLaundry?.laundry_items || [];
    const currentItems = currentLaundry?.laundry_items || [];

    const hasMismatch = currentItems.some(
        (item) => item.quantity_warehouse_confirmation !== item.quantity_pickup
    );

    const getAllRelevantItems = () => {
        const allItemsSet = new Set();

        latestItems.forEach((item) => allItemsSet.add(item.name));
        currentItems.forEach((item) => allItemsSet.add(item.name));
        Object.keys(standardQuantities).forEach((itemName) => allItemsSet.add(itemName));

        const allItemsArray = Array.from(allItemsSet).map((itemName) => {
            const standardQuantity = standardQuantities[itemName] || 0;
            const latestItem = latestItems.find((item) => item.name === itemName) || {};
            const currentItem = currentItems.find((item) => item.name === itemName) || {};

            return {
                name: itemName,
                standardQuantity,
                latestItem,
                currentItem,
            };
        });

        return sortItemsByPriority(allItemsArray);
    };

    // Asia/Dubai render (item 8) — same dd/mm/yyyy visual as before.
    const formatDate = (date) => (date ? dubaiDate(date) || 'N/A' : 'N/A');

    return (
        <div className="laundry-details-table-container">
            {/* LEFT ARROW = previous laundry */}
            {latestLaundry?.booking_id && (
                <button
                    type="button"
                    className="laundry-nav-arrow laundry-nav-arrow-left"
                    onClick={() => navigate(`/laundry/${latestLaundry.booking_id}`)}
                    title="Previous laundry count"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
            )}

            {/* RIGHT ARROW = next laundry */}
            {nextLaundry?.booking_id && (
                <button
                    type="button"
                    className="laundry-nav-arrow laundry-nav-arrow-right"
                    onClick={() => navigate(`/laundry/${nextLaundry.booking_id}`)}
                    title="Next laundry count"
                >
                    <FontAwesomeIcon icon={faArrowRight} />
                </button>
            )}

            <table className="laundry-details-table">
                <thead>
                    <tr>
                        <th></th>
                        <th colSpan="2">
                            {latestLaundry?.created_at ? (
                                <a href={`/laundry/${latestLaundry.booking_id}`}>
                                    {formatDate(latestLaundry.created_at)}
                                </a>
                            ) : 'N/A'}
                        </th>
                        <th colSpan="2">
                            {currentLaundry?.created_at ? (
                                <a href={`/laundry/${currentLaundry.booking_id}`}>
                                    {formatDate(currentLaundry.created_at)}
                                </a>
                            ) : 'N/A'}
                        </th>
                        {hasMismatch && <th></th>}
                    </tr>
                    <tr>
                        <th style={{ textAlign: 'left' }}>Item Name (SQ)</th>
                        <th>Last Drop</th>
                        <th>Last Pick</th>
                        <th>Drop</th>
                        <th>Pick (IC)</th>
                        {hasMismatch && <th>WH</th>}
                    </tr>
                </thead>
                <tbody>
                    {getAllRelevantItems().map(({ name, standardQuantity, latestItem, currentItem }) => {
                        const standardQty = Number(standardQuantity ?? 0);

                        const currentDropoff = currentItem.quantity_dropoff != null ? Number(currentItem.quantity_dropoff) : null;
                        const currentPickup = currentItem.quantity_pickup != null ? Number(currentItem.quantity_pickup) : null;

                        const lastDropoff = latestItem.quantity_dropoff != null ? Number(latestItem.quantity_dropoff) : null;

                        // Highlight Drop when it does NOT match SQ
                        const isDropoffMismatchSQ =
                            currentDropoff != null && Number.isFinite(standardQty) && currentDropoff !== standardQty;

                        // RED highlight when Pickup does NOT match Last Drop
                        const isPickupMismatchLastDrop =
                            currentPickup != null && lastDropoff != null && currentPickup !== lastDropoff;

                        const isWarehouseMismatch =
                            currentItem.quantity_warehouse_confirmation !== currentItem.quantity_pickup;

                        return (
                            <tr key={name}>
                                <td className="first-column" style={{ textAlign: 'left' }}>
                                    {name} (
                                    <span className={isDropoffMismatchSQ ? 'warning-highlight' : ''}>
                                        {standardQuantity}
                                    </span>
                                    )
                                </td>

                                <td className={isPickupMismatchLastDrop ? 'mismatch-highlight' : ''}>
                                    {latestItem.quantity_dropoff ?? '-'}
                                </td>

                                <td>
                                    {latestItem.quantity_pickup ?? '-'}
                                </td>

                                <td className={isDropoffMismatchSQ ? 'warning-highlight' : ''}>
                                    {currentItem.quantity_dropoff ?? '-'}
                                </td>

                                <td className={isPickupMismatchLastDrop ? 'mismatch-highlight' : ''}>
                                    {currentItem.quantity_pickup ?? '-'}
                                    {currentItem.cleaner_quantity_pickup != null &&
                                        Number(currentItem.cleaner_quantity_pickup) !== Number(currentItem.quantity_pickup)
                                        ? ` (${currentItem.cleaner_quantity_pickup})`
                                        : ''}
                                </td>

                                {hasMismatch && (
                                    <td className={isWarehouseMismatch ? 'mismatch' : ''}>
                                        {currentItem.quantity_warehouse_confirmation ?? 'Not Confirmed'}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default LaundryDetailsTable;